import { jwtDecode } from 'jwt-decode';

/**
 * Get the JWT token from session storage
 * @returns {string|null} JWT token or null
 */
export const getToken = () => {
  try {
    // First try the new 'token' key
    let token = sessionStorage.getItem("token");
    
    // Fallback to old 'user' structure for backward compatibility
    if (!token) {
      const user = sessionStorage.getItem("user");
      if (user) {
        const userData = JSON.parse(user);
        token = userData?.message || null;
      }
    }
    
    return token;
  } catch (error) {
    console.error('Error getting token:', error);
    return null;
  }
};

/**
 * Get decoded token data with expiration check
 * @returns {object|null} Decoded token payload or null if expired/invalid
 */
export const getDecodedToken = () => {
  const token = getToken();
  if (!token) return null;
  
  try {
    const decoded = jwtDecode(token);
    // Check if token is expired
    if (decoded.exp && decoded.exp * 1000 < Date.now()) {
      console.warn('Token has expired');
      // Log the auto-logout due to expiration
      try {
        fetch(`${process.env.REACT_APP_BASE_URL}hec/log-info/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => {}); // Silently fail if API call fails
      } catch (error) {
        // Ignore errors during logout logging
      }
      sessionStorage.clear();
      return null;
    }
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Get user role from JWT token
 * IMPORTANT: This is for UI purposes only. Backend must validate all permissions.
 * The role comes from the JWT token which is signed by the backend and cannot be tampered with.
 * @returns {string|null} User role or null
 */
export const getUserRole = () => {
  const decoded = getDecodedToken();
  // Role is now embedded in the JWT token by the backend
  return decoded?.role || null;
};

/**
 * Get user ID from JWT token
 * @returns {string|null} User ID or null
 */
export const getUserId = () => {
  const decoded = getDecodedToken();
  return decoded?.userId || null;
};

/**
 * Get user email from JWT token
 * @returns {string|null} User email or null
 */
export const getUserEmail = () => {
  const decoded = getDecodedToken();
  return decoded?.email || decoded?.sub || null;
};

/**
 * Get full user info from JWT token
 * @returns {object|null} User info object or null
 */
export const getUserInfo = () => {
  const decoded = getDecodedToken();
  if (!decoded) return null;
  
  return {
    userId: decoded.userId,
    email: decoded.email || decoded.sub,
    firstName: decoded.firstName,
    lastName: decoded.lastName,
    role: decoded.role,
    roleId: decoded.roleId,
  };
};

/**
 * Check if user is authenticated (has valid token)
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
  return getDecodedToken() !== null;
};

/**
 * Check if user has a specific role
 * IMPORTANT: This is for UI purposes only. Backend must validate all permissions.
 * @param {string|string[]} roles - Role or array of roles to check
 * @returns {boolean} True if user has the role
 */
export const hasRole = (roles) => {
  const userRole = getUserRole();
  if (!userRole) return false;
  
  if (Array.isArray(roles)) {
    return roles.includes(userRole);
  }
  
  return userRole === roles;
};

/**
 * Check if user is admin or DG
 * @returns {boolean} True if user is admin or DG
 */
export const isAdminOrDG = () => {
  return hasRole(['Admin', 'DG']);
};

/**
 * Logout user and clear session storage
 */
export const logout = async () => {
  try {
    const token = getToken();
    if (token) {
      // Call backend to log the logout
      await fetch(`${process.env.REACT_APP_BASE_URL}hec/log-info/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(err => console.error('Failed to log logout:', err));
    }
  } catch (error) {
    console.error('Error during logout:', error);
  } finally {
    sessionStorage.clear();
    window.location.href = "/";
  }
};

/**
 * Get authorization header for API requests
 * @returns {object} Headers object with Authorization
 */
export const getAuthHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// /**
//  * Get the stored user object (for backward compatibility)
//  * This reads the full user object from sessionStorage
//  * DEPRECATED: Use specific getter functions instead (getUserInfo, getUserRole, etc.)
//  * @returns {object|null} User object or null
//  */
// export const getStoredUserData = () => {
//   try {
//     const user = sessionStorage.getItem("user");
//     return user ? JSON.parse(user) : null;
//   } catch (error) {
//     console.error('Error parsing user data:', error);
//     return null;
//   }
// };